import {
  Card,
  Row,
  Table,
  Col,
  Input,
  message,
  Spin,
  Modal,
  Tooltip,
  Button,
} from "antd";
import type { ColumnsType } from "antd/es/table"; // Add this import
import { SearchOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RiAddBoxFill } from "react-icons/ri";
import AddDesignationsModal from "./Add";
import EditDesignationsModal from "./Edit";
import { hasPermission } from "../../utils/HasPermission";
import {
  fetchDesignations,
  deleteDesignation,
  updateDesignation,
} from "../../utils/API";
import { FaPen, FaTrashAlt } from "react-icons/fa";

interface Designation {
  _id: string;
  name: string;
  isActive: boolean;
}

export const DesignationTable = () => {
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [dataSource, setDataSource] = useState<Designation[]>([]);
  const [filteredData, setFilteredData] = useState<Designation[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState<Designation | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getDesignations = async () => {
    setLoading(true);
    try {
      const response: any = await fetchDesignations(searchText, page);
      if (response.data) {
        const sortedData = response.data
          .sort((a: any, b: any) => (b._id > a._id ? 1 : -1))
          .map((designation: any, index: number) => ({
            ...designation,
            serialNumber: index + 1,
          }));

        setDataSource(sortedData);
        setFilteredData(sortedData);
      } else {
        setDataSource([]);
        setFilteredData([]);
        message.error("No designations found.");
      }
    } catch (error) {
      message.error("Network error. Please try again.");
      setDataSource([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDesignations();
  }, [page, searchText]);

  useEffect(() => {
    const filtered = dataSource.filter((designation) => {
      const searchValue = searchText.toLowerCase();
      return designation.name?.toLowerCase().includes(searchValue) || "";
    });
    setFilteredData(filtered);
  }, [searchText, dataSource]);

  const handleEdit = async (record: Designation) => {
    if (!record._id) {
      message.error("Invalid designation ID");
      return;
    }
    setCurrentTask(record); // This should contain all the data you need
    setIsEditModalVisible(true);
  };

  const handleDeleteConfirmation = (id: string) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response: any = await deleteDesignation(deleteId);
      if (response) {
        message.success("Designation deleted successfully");
        setFilteredData((prevData) =>
          prevData.filter((item) => item._id !== deleteId),
        );
        setDataSource((prevData) =>
          prevData.filter((item) => item._id !== deleteId),
        );
        setDeleteId(null);
      }
    } catch (error) {
      message.error("Error deleting designation. Please try again.");
    }
  };

  const handleToggleStatus = async (roleId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      await updateDesignation(roleId, { isActive: newStatus });
      message.success(
        `Designation ${newStatus ? "Activated" : "Deactivated"} successfully!`,
      );

      setDataSource((prev) =>
        prev.map((role) =>
          role._id === roleId ? { ...role, isActive: newStatus } : role,
        ),
      );
      setFilteredData((prev) =>
        prev.map((role) =>
          role._id === roleId ? { ...role, isActive: newStatus } : role,
        ),
      );
    } catch (error) {
      message.error("Failed to update designation status.");
    }
  };

  // Use ColumnsType for proper TypeScript typing
  const columns: ColumnsType<Designation> = [
    {
      title: "#",
      dataIndex: "serialNumber",
      key: "serialNumber",
      render: (text: number) => text || "-",
    },
    {
      title: "Designation Name",
      dataIndex: "name",
      key: "name",
      render: (text: string) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "center", // This is now type-safe
      render: (isActive: boolean, record: Designation) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <label
            style={{
              position: "relative",
              display: "inline-block",
              width: 40,
              height: 22,
              backgroundColor: isActive ? "#001529" : "#f0f0f0",
              borderRadius: 13,
              cursor: "pointer",
              transition: "background-color 0.3s ease",
              boxShadow: isActive
                ? "inset 0 0 0 2px #001529"
                : "inset 0 0 0 1px #ccc",
            }}
          >
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => handleToggleStatus(record._id, isActive)}
              style={{ display: "none" }}
            />
            <span
              style={{
                position: "absolute",
                width: 21,
                height: 19,
                left: isActive ? "calc(99% - 21px)" : 2,
                top: 1,
                backgroundColor: "#ffffff",
                borderRadius: "50%",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                transition: "left 0.3s ease, box-shadow 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: "bold",
                color: isActive ? "#001529" : "#666",
              }}
            >
              {isActive ? "ON" : "OFF"}
            </span>
          </label>
        </div>
      ),
    },
    ...(hasPermission("designations", "update") ||
    hasPermission("designations", "delete")
      ? ([
          {
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (_: any, record: Designation) => (
              <div
                className="d-flex"
                style={{ gap: "8px", alignItems: "center" }}
              >
                {hasPermission("designations", "update") && (
                  <Tooltip title="Edit">
                    <button
                      onClick={() => handleEdit(record)}
                      className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                             shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
                             hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
                             active:translate-y-[1px] active:shadow-none 
                             text-white flex items-center justify-center 
                             transition-all duration-200 ease-in-out group"
                    >
                      <FaPen
                        className="group-hover:scale-110 transition-transform duration-200"
                        size={12}
                      />
                    </button>
                  </Tooltip>
                )}
                {hasPermission("designations", "delete") && (
                  <Tooltip title="Delete">
                    <button
                      onClick={() => handleDeleteConfirmation(record._id)}
                      className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
                             shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
                             hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
                             active:translate-y-1 active:shadow-none 
                             text-white flex items-center justify-center 
                             transition-all duration-200 group"
                    >
                      <FaTrashAlt
                        className="group-hover:scale-110 transition-transform"
                        size={12}
                      />
                    </button>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ] as ColumnsType<Designation>[number][])
      : []),
  ];

  return (
    <div>
      <Row
        className="m-2"
        align="middle"
        justify="space-between"
        gutter={[16, 16]}
      >
        {/* Left Side - Heading */}
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <h2 className="headername">Designations</h2>
        </Col>

        <Col xs={24} md={10}>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            {/* Search Input (Flexible Width) */}
            <Input
              prefix={<SearchOutlined />}
              size="large"
              placeholder="Search..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="flex-1"
              style={{ height: "33px", width: 200 }}
            />

            <Button
              type="primary"
              onClick={() => setIsModalOpen(true)}
              className="allbuttons "
            >
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add</span>
            </Button>

            <AddDesignationsModal
              visible={isModalOpen}
              onTaskCreated={() => {
                setIsModalOpen(false);
                getDesignations();
              }}
              onClose={() => setIsModalOpen(false)}
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Card className="w-100">
          <Col>
            <Spin spinning={loading}>
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="_id"
                pagination={{
                  pageSize: 10,
                  current: page,
                  onChange: (pageNumber) => setPage(pageNumber),
                  showSizeChanger: false,
                  total: filteredData.length,
                }}
              />
            </Spin>
          </Col>
        </Card>
      </Row>

      <Modal
        open={!!deleteId} // <-- This makes the modal open when deleteId has a value
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Designations?</p>
      </Modal>

      {currentTask && (
        <EditDesignationsModal
          visible={isEditModalVisible}
          initialValues={{
            _id: currentTask._id, // Make sure this is passed
            name: currentTask.name,
          }}
          onClose={() => setIsEditModalVisible(false)}
          onSuccess={() => {
            setIsEditModalVisible(false);
            getDesignations();
          }}
        />
      )}
    </div>
  );
};

export const DESIGNATION_TABLE_COLUMNS = ["name", "isActive"];
